import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { TimeUtil } from 'rosbag';
import * as PPBTimeUtil from 'src/app/utils/time-utils';
import { IRosbagTopic } from 'src/app/models_new/types/rosbag/IRosbagTopic';
import { take } from 'rxjs/operators';
import { clamp } from 'three/src/math/MathUtils';
import { IRosbagInfo } from '../../models_new/types/rosbag/IRosbagInfo';
import { ReadResult, Time } from '../../models_new/types/rosbag/rosbag-types';
import { ObjectUtils } from '../../utils/object';

export const defaultBagInfo: IRosbagInfo = {
  topics: [],
  totalNrMsgs: 0,
  startTime: { sec: 0, nsec: 0 } as Time,
  endTime: { sec: 0, nsec: 0 } as Time,
};

export class RosbagBuffer {
  /**
   * Progress for bag reading.
   * -1 = uninitialized
   * 0 = initialized and reading will begin
   * (> 0 && < 100) = reading in progress
   * 100 = reading finished
   */
  readProgress$ = new BehaviorSubject<number>(-1);

  // Independent error observable to limmit spamming of errors in console.
  error$ = new Subject<Error>();

  // Info about the bag loaded, contains start and end times, topics and total nr messages.
  info: IRosbagInfo = ObjectUtils.cloneObject(defaultBagInfo);

  // All messages in the bag.
  messages = new Array<ReadResult>();

  // Message indexes marked as "keyframe", grip and drop box events.
  keyframes: number[] = [];

  destroy$ = new ReplaySubject<boolean>(1);

  constructor(info?: IRosbagInfo) {
    if (info) {
      this.setInfo(info);
    }

    this.destroy$.pipe(take(1)).subscribe(() => {
      this.clear();
    });
  }

  setInfo(info: IRosbagInfo): void {
    this.info = info;
  }

  isMsgKeyframe(msg: ReadResult): boolean {
    return (
      Object.keys(msg.message).includes('keyframe') && msg.message['keyframe']
    );
  }

  setMessages(messages: ReadResult[], remapKeyframes?: boolean): void {
    this.messages = messages;

    // Remap all keyframes if requested
    if (remapKeyframes) {
      this.remapKeyframes();
    }
  }

  setMessageAtIndex(
    index: number,
    msg: ReadResult,
    remapKeyframes?: boolean
  ): void {
    if (index < 0 || index >= this.messages.length) {
      console.warn('Index out of bounds! Cannot set message!');
      return;
    }

    this.messages[index] = msg;
    this.messages[index].bufferIndex = index;

    // Remap all keyframes if requested
    if (remapKeyframes) {
      this.remapKeyframes();
    }
  }

  addMessage(msg: ReadResult): void {
    msg.bufferIndex = this.messages.push(msg) - 1;
    if (this.isMsgKeyframe(msg)) {
      this.keyframes.push(msg.bufferIndex);
    }
  }

  remapKeyframes(): void {
    this.keyframes = new Array<number>();
    for (let i = 0; i < this.messages.length; i++) {
      if (this.isMsgKeyframe(this.messages[i])) {
        this.keyframes.push(i);
      }
    }
  }

  getDurationTime(): Time {
    if (!this.info?.startTime || !this.info?.endTime) {
      return { sec: 0, nsec: 0 } as Time;
    }

    return PPBTimeUtil.sub(this.info.endTime, this.info.startTime);
  }

  getDurationSec(): number {
    if (!this.info?.startTime || !this.info?.endTime) {
      return 0;
    }

    return PPBTimeUtil.toSeconds(
      PPBTimeUtil.sub(this.info.endTime, this.info.startTime)
    );
  }

  getDurationMS(): number {
    if (!this.info?.startTime || !this.info?.endTime) {
      return 0;
    }

    return PPBTimeUtil.toMilliseconds(
      PPBTimeUtil.sub(this.info.endTime, this.info.startTime)
    );
  }

  getFirstMessage(topic?: string): ReadResult {
    // Topic not provided, give absolute first message
    if (!topic) {
      return this.messages[0];
    }

    // Topic provided, give first of that topic
    for (const msg of this.messages) {
      if (msg.topic === topic) {
        return msg;
      }
    }

    // No msg with given topic was found
    return null;
  }

  getNrBufferedMsgs(topic?: string): number {
    if (!topic) {
      return this.messages.length;
    }

    // Find all msg with given topic
    let nrMsgs = 0;
    for (const msg of this.messages) {
      // Topic of 'msg' was not correct, skip this message
      if (msg.topic !== topic) {
        continue;
      }

      nrMsgs++;
    }

    return nrMsgs;
  }

  getTotalNrMsgs(): number | undefined {
    return this.info?.totalNrMsgs;
  }

  getMessageIndex(msg: ReadResult): number {
    // No input or invalid input, give invalid index. HA! Serves you right!
    if (!msg) {
      return -1;
    }

    // Find identical message
    for (let i = 0; i < this.messages.length; i++) {
      if (this.messages[i] === msg) {
        return i;
      }
    }

    // Message not found
    return -1;
  }

  getMessages(from: Time, to: Time, topic?: string): Array<ReadResult> {
    // No input or invalid input, ignore
    if (this.messages.length === 0 || !TimeUtil.isLessThan(from, to)) {
      return new Array<ReadResult>();
    }

    // Find all msg within given time interval
    const tempMsgs = new Array<ReadResult>();
    for (const msg of this.messages) {
      if (PPBTimeUtil.isWithin(from, to, msg.timestamp)) {
        // Topic is defined but 'msg' was not in correct topic, skip this message
        if (topic && msg.topic !== topic) {
          continue;
        }

        tempMsgs.push(msg);
      }
    }

    // Sort messages just in case they're out of order.

    return this.sortMessages(tempMsgs);
  }

  getMessagesByIndex(
    from: number,
    to: number,
    topic?: string
  ): Array<ReadResult> {
    // No input or invalid input, ignore
    if (this.messages.length === 0) {
      return new Array<ReadResult>();
    }

    // Find all msg within given index range
    const tempMsgs = new Array<ReadResult>();
    for (
      let i = clamp(from, 0, this.messages.length - 1);
      i <= clamp(to, 0, this.messages.length - 1);
      i++
    ) {
      // Topic is defined but 'msg' was not in correct topic, skip this message
      if (topic && this.messages[i].topic !== topic) {
        continue;
      }

      tempMsgs.push(this.messages[i]);
    }

    // Sort messages just in case they're out of order.
    return this.sortMessages(tempMsgs);
  }

  getMessageByIndex(index: number): ReadResult {
    // No messages or invalid index, give none
    if (
      this.messages.length === 0 ||
      index < 0 ||
      index >= this.messages.length
    ) {
      return null;
    }

    return this.messages[index];
  }

  linearSearch(
    fromIndex: number,
    predicate?: (msg: ReadResult) => boolean,
    direction: 'forward' | 'backward' = 'forward'
  ): ReadResult {
    if (
      (fromIndex < 1 && direction === 'backward') ||
      (fromIndex >= this.messages.length - 1 && direction === 'forward')
    ) {
      console.warn('Boundary index given! Give no message!');
      // No msgs before given index, give none
      return null;
    }

    let index = fromIndex + (direction === 'forward' ? 1 : -1);
    let msg = this.getMessageByIndex(index);

    // No predicate and index was within bounds, return message
    if (!predicate) {
      return msg;
    }

    // Criteria didn't match, continue looking
    while (!predicate(msg) && index > 0) {
      index += direction === 'forward' ? 1 : -1;
      msg = this.getMessageByIndex(index);
    }

    return msg;
  }

  /**
   * Seeks BACK in time to find the last message of each topic in "topics",
   * excluding the given keyframe index in "fromIndex".
   *
   * @param fromIndex { number } - Index to start the search from.
   * @param topics { object } - Keys are topics to look for. Values are predicate functions to match for each topic msg. **NOTE!** If grabAll is true, these are ignored.
   * @param grabAll { boolean } - Wether to grab all or only the first of each topic. Default is **FIRST**. **NOTE!** Overrides per topic conditions.
   * @param toIndex { number } - Last index to search to. Default is **0**.
   * @returns { ReadResult[] } - List of all found messages. NOTE! Some topics might be missing.
   */
  lastOfMsgsInTopics(
    fromIndex: number,
    topicsToLookFor: { [topic: string]: ((msg: ReadResult) => boolean) | null },
    grabAll: boolean = false,
    toIndex: number = 0
  ): ReadResult[] {
    let index = fromIndex - 1;

    // Out of bounds, no messages.
    if (index < 0) {
      return [];
    }

    const msgs: ReadResult[] = [];
    const topics = Object.keys(topicsToLookFor);
    const conditions = Object.values(topicsToLookFor);
    /**
     * Keep going backwards until all messages for all topics are found or
     * no more messages to process.
     */
    while (msgs.length < topics.length && index > toIndex) {
      let msg = this.getMessageByIndex(index);

      if (
        // Message is of a topic we're looking for &&
        topics.includes(msg.topic) &&
        // Should grab all messages or only the first message of each topic
        (!msgs.find((m) => m.topic === msg.topic) || grabAll)
      ) {
        // Only add message if all should be grabbed or it matches the condition or no condition was given.
        if (
          grabAll ||
          (conditions[topics.indexOf(msg.topic)] !== null &&
            conditions[topics.indexOf(msg.topic)](msg)) ||
          conditions[topics.indexOf(msg.topic)] === null
        ) {
          msgs.push(msg);
        }
      }

      index--;
    }

    return this.sortMessages(msgs);
  }

  public getNRKeyframes(): number {
    return this.keyframes.length;
  }

  public findKFIndexFromMessageIndex(msgIndex: number): number | undefined {
    return this.keyframes.indexOf(msgIndex);
  }

  public getKeyframe(index: number): ReadResult | undefined {
    return this.getKeyframeByKFIndex(index);
  }

  public getKeyframeByKFIndex(keyframeIndex: number): ReadResult | undefined {
    // No messages or invalid index, give none
    if (
      this.keyframes.length === 0 ||
      keyframeIndex < 0 ||
      keyframeIndex >= this.keyframes.length
    ) {
      return undefined;
    }

    return this.getMessageByIndex(this.keyframes[keyframeIndex]);
  }

  public getFrameIndexFromKFIndex(keyframeIndex: number): number | undefined {
    // No messages or invalid index, give none
    if (
      this.keyframes.length === 0 ||
      keyframeIndex < 0 ||
      keyframeIndex >= this.keyframes.length
    ) {
      return undefined;
    }

    return this.keyframes[keyframeIndex];
  }

  private sortMessages(msgs: ReadResult[]): ReadResult[] {
    return msgs.sort((a, b) => {
      return TimeUtil.isLessThan(a.timestamp, b.timestamp) ? -1 : 1;
    });
  }

  addTopic(newTopic: IRosbagTopic): void {
    // Check for duplicate
    let found = false;
    for (const topic of this.info.topics) {
      found = found || topic.name === newTopic.name;
    }

    // Not a duplicate, add it
    if (!found) {
      this.info.topics.push(newTopic);
    }
  }

  clear(): void {
    this.info.topics = new Array<IRosbagTopic>();
    this.messages = new Array<ReadResult>();
  }

  destroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
